/**
 * Client API pour pos-merchant-dashboard.
 *
 * Backend  : pos-api-app-prod (FastAPI) — https://api.pos.gofreshpay.com
 *            Staging          — https://api-staging.pos.gofreshpay.com
 * Auth     : JWT Bearer dans Authorization header.
 *            Access token stocké en localStorage (parité avec l'ancien
 *            `posrenewed` pour permettre une transition douce si les 2
 *            frontends coexistent temporairement).
 * Refresh  : POST /auth/refresh avec le Bearer courant renvoie un nouveau
 *            access_token. Pas de refresh_token séparé.
 *
 * NOTE Phase 1 : ce module ne contient que la surface auth. Les endpoints
 * métier (paiements, retraits, cashiers, config, etc.) seront ajoutés
 * feature-par-feature au fur et à mesure du portage des pages depuis
 * `posrenewed`.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.pos.gofreshpay.com";

const ACCESS_TOKEN_KEY = "pos_access_token";
const USER_DATA_KEY = "pos_user_data";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type Role =
  | "merchant"
  | "cashier"
  | "admin"
  | "super_admin"
  | "backoffice"
  | "support"
  | "sales_manager"
  | "commercial"
  | "finance"
  | "recruiter";

export type MeResponse = {
  id: number;
  username: string;
  role: Role;
  merchant_id: number | null;
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  is_active: boolean;
  must_change_password?: boolean;
};

export type LoginRawResponse = {
  access_token: string;
  user_id: number;
  username: string;
  role: string;
  merchant_id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone_number?: string;
  must_change_password?: boolean;
};

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------
let _accessToken: string | null = null;

export function setAccessToken(token: string): void {
  _accessToken = token;
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function getAccessToken(): string | null {
  if (_accessToken) return _accessToken;
  const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (stored) {
    _accessToken = stored;
    return stored;
  }
  return null;
}

export function clearTokens(): void {
  _accessToken = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
}

function saveUser(user: MeResponse): void {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
}

export function getStoredUser(): MeResponse | null {
  const raw = localStorage.getItem(USER_DATA_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as MeResponse;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string): { exp?: number; [k: string]: unknown } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

export function isTokenExpiringSoon(): boolean {
  const t = getAccessToken();
  if (!t) return false;
  const payload = decodeJwtPayload(t);
  if (!payload?.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return (payload.exp as number) - now < 60;
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------
export class ApiError extends Error {
  constructor(
    public status: number,
    public title: string,
    public detail: string,
    public extra?: Record<string, unknown>,
  ) {
    super(detail || title);
    this.name = "ApiError";
  }
}

// ---------------------------------------------------------------------------
// Request helper
// ---------------------------------------------------------------------------
type RequestOptions = {
  method?: string;
  body?: unknown;
  skipAuth?: boolean;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
};

function buildQueryString(query?: Record<string, unknown>): string {
  if (!query) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, skipAuth = false, headers = {}, query } = options;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };
  if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
  }

  // Refresh proactivement si expire dans <60s
  if (!skipAuth && path !== "/auth/refresh" && isTokenExpiringSoon()) {
    await refreshAccessToken();
  }

  const token = getAccessToken();
  if (!skipAuth && token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const fullPath = `${path}${buildQueryString(query)}`;
  const url = fullPath.startsWith("http") ? fullPath : `${API_BASE}${fullPath}`;

  const resp = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "omit",
  });

  // 401 → tenter un refresh + retry une fois
  if (resp.status === 401 && !skipAuth && path !== "/auth/refresh" && path !== "/auth/login") {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retryHeaders = { ...finalHeaders, Authorization: `Bearer ${getAccessToken()}` };
      const retry = await fetch(url, {
        method,
        headers: retryHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: "omit",
      });
      return handleResponse<T>(retry);
    }
    clearTokens();
  }

  return handleResponse<T>(resp);
}

async function handleResponse<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as T;

  let data: unknown = null;
  try {
    data = await resp.json();
  } catch {
    data = null;
  }

  if (!resp.ok) {
    const detail =
      typeof (data as { detail?: unknown })?.detail === "string"
        ? ((data as { detail: string }).detail)
        : `HTTP ${resp.status}`;
    throw new ApiError(resp.status, detail, detail, (data ?? undefined) as Record<string, unknown> | undefined);
  }

  return data as T;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export async function login(username: string, password: string): Promise<MeResponse> {
  const raw = await apiRequest<LoginRawResponse>("/auth/login", {
    method: "POST",
    body: { username, password },
    skipAuth: true,
  });

  setAccessToken(raw.access_token);

  const user: MeResponse = {
    id: raw.user_id,
    username: raw.username,
    role: raw.role as Role,
    merchant_id: raw.merchant_id ?? null,
    firstname: raw.firstname ?? "",
    lastname: raw.lastname ?? "",
    email: raw.email ?? "",
    phone_number: raw.phone_number ?? "",
    is_active: true,
    must_change_password: raw.must_change_password,
  };
  saveUser(user);
  return user;
}

export async function refreshAccessToken(): Promise<boolean> {
  if (!getAccessToken()) return false;
  try {
    const resp = await apiRequest<{ access_token: string }>("/auth/refresh", {
      method: "POST",
      // skipAuth=false but the refresh endpoint expects the *current* bearer
    });
    setAccessToken(resp.access_token);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

export function logout(): void {
  clearTokens();
}

export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await apiRequest("/auth/update-password", {
    method: "POST",
    body: { current_password, new_password },
  });
}

// ---------------------------------------------------------------------------
// Password reset — POST /auth/request-password-reset, /auth/confirm-password-reset
// ---------------------------------------------------------------------------
export async function requestPasswordReset(username_or_email: string): Promise<void> {
  await apiRequest("/auth/request-password-reset", {
    method: "POST",
    body: { username_or_email },
  });
}

export async function confirmPasswordReset(
  reset_code: string,
  new_password: string,
): Promise<void> {
  await apiRequest("/auth/confirm-password-reset", {
    method: "POST",
    body: { reset_code, new_password },
  });
}

// ---------------------------------------------------------------------------
// Dashboard — GET /admin/merchant/balance, /analytics/dashboard/quick-stats,
//             /commissions/merchant-dashboard-summary
// ---------------------------------------------------------------------------
export type MerchantWallet = {
  wallet_id: number;
  currency: string;
  method: string;
  wallet_type: string;
  owner_type: string;
  balance: number;
  balance_str?: string;
  last_updated: string | null;
  wallet_created?: string | null;
};

export type MerchantBalanceResponse = {
  merchant: {
    id: number;
    business_name: string;
    business_email?: string;
    business_phone?: string;
    is_active: boolean;
  };
  wallets: MerchantWallet[];
  summary: {
    total_wallets: number;
    total_by_currency: Record<string, number>;
    currencies_available: string[];
  };
};

export async function getMerchantBalance(merchantId: number): Promise<MerchantBalanceResponse> {
  return apiRequest<MerchantBalanceResponse>("/admin/merchant/balance", {
    query: { merchant_id: merchantId },
  });
}

export type QuickStatsBucket = {
  currency: string;
  total_transactions: number;
  successful_volume: number;
  successful_fees: number;
  failed_transactions?: number;
};

export type QuickStatsResponse = {
  today: QuickStatsBucket[];
  monthly: QuickStatsBucket[];
  totals: {
    today_transactions: number;
    today_failed: number;
    monthly_transactions: number;
  };
};

export async function getQuickStats(): Promise<QuickStatsResponse> {
  return apiRequest<QuickStatsResponse>("/analytics/dashboard/quick-stats");
}

export type DailyComparison = {
  total_tx: number;
  successful: number;
  volume_cdf: number;
  volume_usd: number;
  commission_cdf: number;
  commission_usd: number;
  success_rate: number;
};

export type DashboardSummary = {
  comparison: {
    today: DailyComparison;
    yesterday: DailyComparison;
    changes: {
      tx_change: number;
      volume_cdf_change: number;
      volume_usd_change: number;
      commission_cdf_change: number;
      commission_usd_change: number;
      rate_change: number;
    };
  };
  trend_7d: Array<{
    date: string;
    total_tx: number;
    successful: number;
    failed: number;
    volume: number;
    commission: number;
  }>;
};

export async function getDashboardSummary(): Promise<DashboardSummary> {
  return apiRequest<DashboardSummary>("/commissions/merchant-dashboard-summary");
}

export type DailyTransactionRow = {
  date: string;
  status: string;
  currency: string;
  transaction_count: number;
  total_amount: number;
  total_fees: number;
};

export type DashboardStatsResponse = {
  total_stores: number;
  total_cashiers: number;
  total_pos: number;
  daily_transactions: DailyTransactionRow[];
  cashier_performance: Array<{
    cashier_id: number;
    firstname: string;
    lastname: string;
    email: string;
    store_name: string;
    currency: string;
    total_transactions: number;
    successful_transactions: number;
    total_volume: number;
    total_fees: number;
    success_rate: number;
  }>;
};

export async function getDashboardStats(): Promise<DashboardStatsResponse> {
  return apiRequest<DashboardStatsResponse>("/analytics/dashboard/stats");
}

export type OperatorPerformance = {
  method: string;
  currency: string;
  total_transactions: number;
  successful_transactions: number;
  failed_transactions: number;
  success_rate: number;
  total_volume: number;
  total_fees: number;
};

export async function getOperatorPerformance(): Promise<OperatorPerformance[]> {
  return apiRequest<OperatorPerformance[]>("/analytics/operators/performance");
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------
export type MerchantTransaction = {
  id: number;
  merchant_id: number;
  amount: number;
  fee: number;
  total_amount: number;
  currency: string;
  customer_number: string | null;
  method: string;
  reference: string;
  telco_reference: string | null;
  status: "Successful" | "Failed" | "Pending" | string;
  status_description: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  operation_type: "depot" | "retrait";
};

export async function listMerchantTransactions(): Promise<MerchantTransaction[]> {
  const resp = await apiRequest<MerchantTransaction[] | { detail: string }>("/transactions/super-agent/all");
  return Array.isArray(resp) ? resp : [];
}

// ---------------------------------------------------------------------------
// Retrait (withdrawal) — GET /payments/phone-config, GET /withdrawals/preview-fee,
//                        POST /withdrawals/initiate, POST /withdrawals/resend-otp
// ---------------------------------------------------------------------------
export type MerchantPhoneConfig = {
  method_mpesa?: string;
  method_orange?: string;
  method_airtel?: string;
  method_mpesa_2?: string;
  method_orange_2?: string;
  method_airtel_2?: string;
  dual_withdrawal_numbers?: boolean;
};

export async function getPhoneConfig(): Promise<MerchantPhoneConfig> {
  return apiRequest<MerchantPhoneConfig>("/payments/phone-config");
}

export type PhoneConfigChangeRequest = {
  id?: number;
  requested_method_mpesa: string;
  requested_method_orange: string;
  requested_method_airtel: string;
  requested_method_mpesa_2?: string;
  requested_method_orange_2?: string;
  requested_method_airtel_2?: string;
  status: "pending" | "approved" | "rejected";
  reason?: string;
  rejection_reason?: string;
  created_at?: string;
  processed_at?: string;
};

export async function getPendingPhoneConfigRequest(): Promise<PhoneConfigChangeRequest | null> {
  return apiRequest<PhoneConfigChangeRequest | null>("/payments/phone-config/pending");
}

export type PhoneConfigChangePayload = {
  requested_method_mpesa: string;
  requested_method_orange: string;
  requested_method_airtel: string;
  requested_method_mpesa_2?: string;
  requested_method_orange_2?: string;
  requested_method_airtel_2?: string;
  reason?: string;
};

export async function requestPhoneConfigChange(
  payload: PhoneConfigChangePayload,
): Promise<PhoneConfigChangeRequest> {
  return apiRequest<PhoneConfigChangeRequest>("/payments/phone-config/request-change", {
    method: "POST",
    body: payload,
  });
}

export async function cancelPhoneConfigChange(id: number): Promise<void> {
  await apiRequest(`/payments/phone-config/request-change/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Operations : stores / agents (cashiers) / POS devices
// ---------------------------------------------------------------------------
export type Store = {
  id: number;
  merchant_id: number;
  name: string;
  location: string;
  is_active: boolean;
  has_pos_device?: boolean;
  store_email?: string | null;
  store_phone?: string | null;
  store_type?: "physical" | "online" | "both" | null;
  type?: string | null;
  created_at?: string;
};

export type MerchantUser = {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  role: "merchant" | "cashier" | "admin" | "super_admin" | string;
  is_active: boolean;
  merchant_id?: number | null;
  store_id?: number | null;
  created_at?: string;
  last_login_at?: string | null;
};

export type PosDevice = {
  id: number;
  pos_id: string;
  store_id: number;
  store_name: string;
  location: string;
  is_available: boolean;
  is_super_agent?: boolean;
  callback_url?: string;
  current_user_id?: number | null;
  created_at?: string;
};

export async function listStores(): Promise<Store[]> {
  const r = await apiRequest<{ stores?: Store[] } | Store[]>("/stores/");
  if (Array.isArray(r)) return r;
  return r.stores ?? [];
}

export async function listMerchantUsers(): Promise<MerchantUser[]> {
  const r = await apiRequest<{ users?: MerchantUser[] } | MerchantUser[]>("/users/");
  if (Array.isArray(r)) return r;
  return r.users ?? [];
}

export async function listPosDevices(): Promise<PosDevice[]> {
  const r = await apiRequest<{ pos_list?: PosDevice[] } | PosDevice[]>("/pos/");
  if (Array.isArray(r)) return r;
  return r.pos_list ?? [];
}

export async function toggleUserActive(userId: number, isActive: boolean): Promise<void> {
  await apiRequest(`/users/status/${userId}`, {
    method: "PUT",
    body: { is_active: isActive },
  });
}

export async function togglePosAvailable(posId: string, isAvailable: boolean): Promise<void> {
  await apiRequest(`/pos/${posId}`, {
    method: "PUT",
    body: { is_available: isAvailable },
  });
}

export async function deletePos(posId: string): Promise<void> {
  await apiRequest(`/pos/${posId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Tickets (Support)
// ---------------------------------------------------------------------------
export type TicketMessage = {
  id: string;
  sender_type: "user" | "staff";
  sender_name: string;
  message: string;
  attachments?: Array<{ name: string; type: string; size: number; data_url: string }>;
  created_at: string;
};

export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

export type Ticket = {
  id: string;
  ticket_id: string;
  merchant_code?: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  assigned_to?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
  messages?: TicketMessage[];
  message_count?: number;
  last_user_message_at?: string | null;
  last_staff_message_at?: string | null;
  merchant_viewed_at?: string | null;
  unread_by_merchant?: boolean;
};

export type CreateTicketPayload = {
  subject: string;
  description: string;
  email: string;
  priority: TicketPriority;
  category: string;
};

export type SendMessagePayload = {
  message: string;
  attachments?: Array<{ name: string; type: string; size: number; data_url: string }>;
};

export type TicketStats = {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  urgent_tickets: number;
  average_response_time: number;
};

export async function listTickets(): Promise<Ticket[]> {
  return apiRequest<Ticket[]>("/tickets");
}

export async function getTicket(ticketId: string): Promise<Ticket> {
  return apiRequest<Ticket>(`/tickets/${encodeURIComponent(ticketId)}`);
}

export async function createTicket(payload: CreateTicketPayload): Promise<Ticket> {
  return apiRequest<Ticket>("/tickets", { method: "POST", body: payload });
}

export async function sendTicketMessage(
  ticketId: string,
  payload: SendMessagePayload,
): Promise<TicketMessage> {
  return apiRequest<TicketMessage>(`/tickets/${encodeURIComponent(ticketId)}/messages`, {
    method: "POST",
    body: payload,
  });
}

export async function getTicketCategories(): Promise<string[]> {
  try {
    return await apiRequest<string[]>("/tickets/categories");
  } catch {
    return ["Technical", "Billing", "Account", "Payment", "Withdrawal", "Other"];
  }
}

export async function getTicketStats(): Promise<TicketStats> {
  return apiRequest<TicketStats>("/tickets/stats");
}

// ---------------------------------------------------------------------------
// User profile
// ---------------------------------------------------------------------------
export type UserProfile = {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  role: string;
  merchant_id: number | null;
  created_at?: string;
  updated_at?: string;
};

export async function getUserProfile(): Promise<UserProfile> {
  return apiRequest<UserProfile>("/users/profile/me");
}

export type UpdateUserProfilePayload = {
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
};

export async function updateUserProfile(payload: UpdateUserProfilePayload): Promise<{ message: string }> {
  return apiRequest("/users/profile/me", { method: "PUT", body: payload });
}

export type WithdrawalFeePreview = {
  amount: number;
  currency: string;
  method: string;
  fee_rate: number;
  fee_rate_pct: number;
  fee_amount: number;
  total_to_pay: number;
  amount_customer_receives: number;
  fee_label: string;
  has_fee: boolean;
};

export async function previewWithdrawalFee(params: {
  amount: number;
  currency: string;
  method: string;
}): Promise<WithdrawalFeePreview> {
  return apiRequest<WithdrawalFeePreview>("/withdrawals/preview-fee", { query: params });
}

export type WithdrawalOtpRequired = {
  status: "otp_required";
  message: string;
  reference: string;
  requires_otp: true;
  note?: string;
};

export type WithdrawalPayoutResponse = {
  status: "success" | "pending" | "pending_approval" | string;
  message: string;
  reference: string;
  switch_reference: string | null;
  amount: number;
  currency: string;
  method: string;
  note?: string;
  methods_used?: string[];
};

export type WithdrawalLimitExceeded = {
  limit_exceeded: true;
  weekly_limit: number;
  already_withdrawn: number;
  remaining_limit: number;
  requested_amount: number;
  exceeded_amount: number;
  currency: string;
  period: { week_start: string; week_end: string };
  message: string;
};

export type WithdrawalInitiatePayload = {
  amount: string | number;
  currency: string;
  method: string;
  phone_choice?: 1 | 2;
  otp_code?: string;
  reference?: string;
  otp_reference?: string;
};

export async function initiateWithdrawal(
  payload: WithdrawalInitiatePayload,
): Promise<WithdrawalOtpRequired | WithdrawalPayoutResponse> {
  return apiRequest<WithdrawalOtpRequired | WithdrawalPayoutResponse>("/withdrawals/initiate", {
    method: "POST",
    body: payload,
  });
}

export async function resendWithdrawalOtp(reference: string): Promise<{ status: string; message?: string }> {
  return apiRequest("/withdrawals/resend-otp", {
    method: "POST",
    body: { reference },
  });
}

// ---------------------------------------------------------------------------
// Cashiers balances overview — GET /balance/merchant/cashiers-overview
// ---------------------------------------------------------------------------
export type CashierBalance = {
  cashier_id: number;
  firstname: string;
  lastname: string;
  username: string;
  phone_number: string;
  is_active: boolean;
  store_id: number | null;
  store_name: string | null;
  totals: { CDF: number; USD: number };
  by_method: Record<string, { CDF: number; USD: number }>;
  share_pct: { CDF: number; USD: number };
  wallet_last_updated: string | null;
  last_transaction_at: string | null;
  last_login_at: string | null;
};

export type CashiersOverviewResponse = {
  merchant: { id: number; business_name: string };
  cashiers: CashierBalance[];
  totals_by_currency: { CDF: number; USD: number };
  counts: { total: number; active: number; with_balance: number };
  retrieved_at: string;
};

export async function getCashiersOverview(): Promise<CashiersOverviewResponse> {
  return apiRequest<CashiersOverviewResponse>("/balance/merchant/cashiers-overview");
}

// ---------------------------------------------------------------------------
// POS locations (Leaflet map) — GET /pos/locations/merchant/my-pos
// ---------------------------------------------------------------------------
export type PosLocationLive = {
  latitude: number | null;
  longitude: number | null;
  accuracy_m: number | null;
  battery_level: number | null;
  network_type: string | null;
  captured_at: string | null;
  received_at: string | null;
  is_stale: boolean;
};

export type PosLocationItem = {
  pos_id: string;
  store_id: number;
  store_name: string | null;
  store_location: string | null;
  is_available: boolean;
  current_user: {
    id: number;
    firstname: string | null;
    lastname: string | null;
    username: string | null;
  } | null;
  live: PosLocationLive | null;
  pings_24h: number;
};

export type PosLocationsResponse = {
  merchant_id: number;
  pos: PosLocationItem[];
  counts: { total: number; with_gps: number; stale: number };
  retrieved_at?: string;
};

export async function getPosLocations(): Promise<PosLocationsResponse> {
  return apiRequest<PosLocationsResponse>("/pos/locations/merchant/my-pos");
}

